import { useMemo, useState } from 'react';
import {
  ArrowsClockwise,
  CheckCircle,
  CurrencyCircleDollar,
  Scales,
  ShieldWarning,
  Warning,
} from '@phosphor-icons/react';

import {
  useImportBankStatementLines,
  useReconciliationSnapshot,
  useResolveReconciliationFinding,
  useRunL1Reconciliation,
  useRunL2Reconciliation,
  useRunL3Reconciliation,
  useRunL4Reconciliation,
  useUpdateEInvoiceAck,
  type ImportBankStatementLinePayload,
  type ReconciliationFinding,
  type ReconciliationFindingSeverity,
  type ResolveReconciliationFindingAction,
} from '@/features/reconciliation';
import { useCurrentBranch } from '@/shared/context/TenantContext';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { StatusBadge, type StatusVariant } from '@/shared/ui/StatusBadge';
import { formatDateTime, formatMoney } from '@/shared/utils/formatters';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function ReconciliationPage(): JSX.Element {
  const branch = useCurrentBranch();
  const snapshot = useReconciliationSnapshot();
  const bankImporter = useImportBankStatementLines();
  const efaturaAck = useUpdateEInvoiceAck();
  const runL1 = useRunL1Reconciliation();
  const runL2 = useRunL2Reconciliation();
  const runL3 = useRunL3Reconciliation();
  const runL4 = useRunL4Reconciliation();
  const resolver = useResolveReconciliationFinding();
  const [businessDate, setBusinessDate] = useState(today);
  const [bankLinesJson, setBankLinesJson] = useState(sampleBankLinesJson);
  const [bankImportError, setBankImportError] = useState<string | null>(null);
  const [efaturaAckJson, setEfaturaAckJson] = useState(sampleEfaturaAckJson);
  const [efaturaAckError, setEfaturaAckError] = useState<string | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState<Record<string, string>>({});
  const findings = snapshot.data?.findings ?? [];
  const latestRun = snapshot.data?.runs[0] ?? null;
  const openFindings = findings.filter((finding) => finding.status === 'open');
  const totals = useMemo(() => summarizeFindings(openFindings), [openFindings]);

  function handleRunL1(): void {
    runLayer(runL1.mutate);
  }

  function handleRunL2(): void {
    runLayer(runL2.mutate);
  }

  function handleRunL3(): void {
    runLayer(runL3.mutate);
  }

  function handleRunL4(): void {
    runLayer(runL4.mutate);
  }

  function handleImportBankLines(): void {
    setBankImportError(null);
    try {
      const parsed = JSON.parse(bankLinesJson) as unknown;
      if (!Array.isArray(parsed)) {
        setBankImportError('JSON bir array olmalı.');
        return;
      }
      const lines = parsed.map((line) => normalizeBankLine(line, businessDate));
      bankImporter.mutate({ lines });
    } catch {
      setBankImportError('Geçerli JSON girin.');
    }
  }

  function handleUpdateEInvoiceAck(): void {
    setEfaturaAckError(null);
    try {
      const parsed = JSON.parse(efaturaAckJson) as {
        invoiceId?: string;
        providerStatus?: 'not_submitted' | 'submitted' | 'accepted' | 'rejected' | 'cancelled';
        ettn?: string | null;
      };
      if (!parsed.invoiceId || !parsed.providerStatus) {
        setEfaturaAckError('invoiceId ve providerStatus zorunlu.');
        return;
      }
      efaturaAck.mutate({
        invoiceId: parsed.invoiceId,
        providerStatus: parsed.providerStatus,
        ettn: parsed.ettn ?? null,
        providerPayload: parsed,
      });
    } catch {
      setEfaturaAckError('Geçerli JSON girin.');
    }
  }

  function runLayer(
    mutate: (payload: { branchId: string | null; businessDate: string }) => void,
  ): void {
    mutate({
      branchId: branch?.branchId ?? null,
      businessDate,
    });
  }

  function updateResolutionNote(findingId: string, value: string): void {
    setResolutionNotes((prev) => ({ ...prev, [findingId]: value }));
  }

  function submitResolution(
    finding: ReconciliationFinding,
    action: ResolveReconciliationFindingAction,
  ): void {
    const fallbackReason = `Reconciliation console ${action} action for ${finding.findingType}.`;
    const reason = resolutionNotes[finding.id]?.trim() || fallbackReason;
    resolver.mutate({ findingId: finding.id, payload: { action, reason } });
  }

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-biz-content-lg px-biz-card-lg py-biz-content-lg">
      <header className="rounded-biz-card border border-biz-border bg-gradient-to-br from-biz-surface-elevated to-biz-surface p-biz-card-lg shadow-biz-sm">
        <div className="flex flex-col gap-biz-content-lg lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-biz-content-lg">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-biz-btn bg-biz-primary text-biz-primary-foreground">
              <Scales size={24} weight="fill" aria-hidden="true" />
            </div>
            <div>
              <p className="text-biz-xs font-semibold uppercase tracking-[0.22em] text-biz-primary">
                ADR-115 L1
              </p>
              <h1 className="mt-1 text-biz-2xl font-bold biz-heading">Finansal mutabakat</h1>
              <p className="mt-1 max-w-3xl text-biz-sm text-biz-muted-foreground">
                POS sipariş toplamları ile tender kayıtlarını karşılaştırır, açık sapmaları P0/P1
                finding olarak üretir ve çözüm aksiyonlarını audit izine bağlar.
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-biz-content sm:flex-row sm:items-center">
            <label className="flex flex-col gap-1 text-biz-xs font-semibold uppercase tracking-wide text-biz-muted-foreground">
              İş günü
              <input
                type="date"
                value={businessDate}
                onChange={(event) => setBusinessDate(event.target.value)}
                className="min-h-biz-touch rounded-biz-btn border border-biz-border bg-biz-surface px-biz-content text-biz-base font-medium text-biz-foreground"
              />
            </label>
            <Button
              type="button"
              onClick={handleRunL1}
              loading={runL1.isPending}
              leftIcon={<ArrowsClockwise size={18} />}
            >
              L1 çalıştır
            </Button>
            <Button
              type="button"
              intent="secondary"
              onClick={handleRunL2}
              loading={runL2.isPending}
              leftIcon={<CurrencyCircleDollar size={18} />}
            >
              L2 çalıştır
            </Button>
            <Button
              type="button"
              intent="secondary"
              onClick={handleRunL3}
              loading={runL3.isPending}
              leftIcon={<Scales size={18} />}
            >
              L3 çalıştır
            </Button>
            <Button
              type="button"
              intent="secondary"
              onClick={handleRunL4}
              loading={runL4.isPending}
              leftIcon={<CheckCircle size={18} />}
            >
              L4 çalıştır
            </Button>
          </div>
        </div>
      </header>

      <section className="grid grid-cols-2 gap-biz-content-lg lg:grid-cols-5">
        <Metric
          icon={<ShieldWarning size={18} weight="fill" />}
          tone={totals.openCount > 0 ? 'warning' : 'success'}
          label="Açık finding"
          value={`${totals.openCount}`}
        />
        <Metric
          icon={<Warning size={18} weight="fill" />}
          tone={totals.p0Count > 0 ? 'danger' : 'neutral'}
          label="P0"
          value={`${totals.p0Count}`}
        />
        <Metric
          icon={<CurrencyCircleDollar size={18} weight="fill" />}
          tone={Math.abs(totals.deltaMinor) > 0 ? 'warning' : 'success'}
          label="Net sapma"
          value={formatMoney(totals.deltaMinor / 100, totals.currency)}
          emphasis
        />
        <Metric
          icon={<CheckCircle size={18} weight="fill" />}
          tone={latestRun?.status === 'succeeded' ? 'success' : latestRun ? 'warning' : 'neutral'}
          label="Son koşum"
          value={latestRun ? runStatusLabel(latestRun.status) : 'Yok'}
        />
        <Metric
          icon={<Scales size={18} weight="fill" />}
          tone={latestRun?.layer === 'L2' || latestRun?.layer === 'L3' ? 'info' : 'neutral'}
          label="Son katman"
          value={latestRun?.layer ?? 'Yok'}
        />
      </section>

      {runL1.isError || runL2.isError || runL3.isError || runL4.isError ? (
        <Card>
          <CardBody className="text-biz-sm text-biz-danger">
            Mutabakat çalıştırılamadı. Branch, iş günü ve yetki bağlamını kontrol edin.
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <div>
            <h2 className="font-semibold biz-heading">L3 settlement import</h2>
            <p className="text-biz-sm text-biz-muted-foreground">
              iyzico/banka settlement satırlarını içe aktarır. L3 koşumu bu satırları
              `payment_intents.provider_payment_id` ile eşleştirir.
            </p>
          </div>
        </CardHeader>
        <CardBody className="grid gap-biz-content-lg lg:grid-cols-[1fr_auto]">
          <textarea
            value={bankLinesJson}
            onChange={(event) => setBankLinesJson(event.target.value)}
            className="min-h-[136px] rounded-biz-card border border-biz-border bg-biz-surface-elevated px-biz-content py-biz-content font-mono text-biz-xs"
            aria-label="Bank statement lines JSON"
          />
          <div className="flex flex-col gap-biz-content">
            <Button
              type="button"
              onClick={handleImportBankLines}
              loading={bankImporter.isPending}
              leftIcon={<CurrencyCircleDollar size={18} />}
            >
              Satırları içe aktar
            </Button>
            <p className="max-w-[280px] text-biz-xs text-biz-muted-foreground">
              Alanlar: source, statementDate, pspReference veya bankReference, amountMinor, currency.
            </p>
            {bankImportError ? <p className="text-biz-xs text-biz-danger">{bankImportError}</p> : null}
            {bankImporter.data ? (
              <p className="text-biz-xs text-biz-success">
                {bankImporter.data.importedCount} yeni, {bankImporter.data.duplicateCount} tekrar.
              </p>
            ) : null}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <div>
            <h2 className="font-semibold biz-heading">L4 e-Fatura ack</h2>
            <p className="text-biz-sm text-biz-muted-foreground">
              Provider webhook/poll sonucunu manuel içe aktarır. L4 koşumu paid e-fatura
              kayıtlarında ETTN ve provider kabul durumunu kontrol eder.
            </p>
          </div>
        </CardHeader>
        <CardBody className="grid gap-biz-content-lg lg:grid-cols-[1fr_auto]">
          <textarea
            value={efaturaAckJson}
            onChange={(event) => setEfaturaAckJson(event.target.value)}
            className="min-h-[112px] rounded-biz-card border border-biz-border bg-biz-surface-elevated px-biz-content py-biz-content font-mono text-biz-xs"
            aria-label="E-fatura acknowledgement JSON"
          />
          <div className="flex flex-col gap-biz-content">
            <Button
              type="button"
              onClick={handleUpdateEInvoiceAck}
              loading={efaturaAck.isPending}
              leftIcon={<CheckCircle size={18} />}
            >
              Ack güncelle
            </Button>
            <p className="max-w-[280px] text-biz-xs text-biz-muted-foreground">
              Alanlar: invoiceId, providerStatus, accepted ise ettn.
            </p>
            {efaturaAckError ? <p className="text-biz-xs text-biz-danger">{efaturaAckError}</p> : null}
            {efaturaAck.data ? <p className="text-biz-xs text-biz-success">Ack kaydedildi.</p> : null}
          </div>
        </CardBody>
      </Card>

      <section className="grid grid-cols-1 gap-biz-content-lg xl:grid-cols-[1fr_360px]">
        <Card className="overflow-hidden">
          <CardHeader>
            <div>
              <h2 className="font-semibold biz-heading">Açık mutabakat findingleri</h2>
              <p className="text-biz-sm text-biz-muted-foreground">
                L1 order/tender, L2 card tender/payment intent, L3 provider settlement/banka
                satırlarını, L4 e-fatura ETTN/provider ack durumunu karşılaştırır.
              </p>
            </div>
          </CardHeader>
          <CardBody className="space-y-biz-content">
            {snapshot.isLoading ? (
              <p className="text-biz-sm text-biz-muted-foreground">Mutabakat kayıtları yükleniyor...</p>
            ) : null}
            {snapshot.isError ? (
              <p className="text-biz-sm text-biz-danger">Mutabakat snapshot okunamadı.</p>
            ) : null}
            {!snapshot.isLoading && openFindings.length === 0 ? (
              <div className="rounded-biz-card border border-biz-success-border bg-biz-success-subtle p-biz-card text-biz-sm text-biz-success">
                Açık finding yok. Son L1 koşumu temiz veya henüz finding üretmedi.
              </div>
            ) : null}
            {openFindings.map((finding) => (
              <FindingCard
                key={finding.id}
                finding={finding}
                note={resolutionNotes[finding.id] ?? ''}
                isResolving={resolver.isPending}
                onNoteChange={(value) => updateResolutionNote(finding.id, value)}
                onResolve={(action) => submitResolution(finding, action)}
              />
            ))}
          </CardBody>
        </Card>

        <Card className="h-fit">
          <CardHeader>
            <h2 className="font-semibold biz-heading">Son koşumlar</h2>
            <p className="text-biz-sm text-biz-muted-foreground">
              Aynı iş günü ve scope tekrar çalıştırıldığında açık findingler yeniden hesaplanır.
            </p>
          </CardHeader>
          <CardBody className="space-y-biz-content">
            {(snapshot.data?.runs ?? []).map((run) => (
              <div key={run.id} className="rounded-biz-card border border-biz-border bg-biz-surface p-biz-content">
                <div className="flex items-center justify-between gap-biz-content">
                  <StatusBadge variant={runStatusVariant(run.status)} label={runStatusLabel(run.status)} />
                  <span className="text-biz-xs text-biz-muted-foreground">{run.layer}</span>
                </div>
                <p className="mt-biz-content text-biz-sm font-semibold biz-heading">
                  {formatDateTime(run.startedAt)}
                </p>
                <p className="mt-1 text-biz-xs text-biz-muted-foreground">
                  Finding: {run.findingCount} · P0: {run.p0Count} · P1: {run.p1Count}
                </p>
              </div>
            ))}
            {!snapshot.isLoading && (snapshot.data?.runs.length ?? 0) === 0 ? (
              <p className="text-biz-sm text-biz-muted-foreground">Henüz reconciliation koşumu yok.</p>
            ) : null}
          </CardBody>
        </Card>
      </section>
    </div>
  );
}

function summarizeFindings(findings: ReconciliationFinding[]): {
  openCount: number;
  p0Count: number;
  deltaMinor: number;
  currency: string;
} {
  return findings.reduce(
    (acc, finding) => ({
      openCount: acc.openCount + 1,
      p0Count: acc.p0Count + (finding.severity === 'P0' ? 1 : 0),
      deltaMinor: acc.deltaMinor + finding.deltaMinor,
      currency: finding.currency || acc.currency,
    }),
    { openCount: 0, p0Count: 0, deltaMinor: 0, currency: 'TRY' },
  );
}

function severityVariant(severity: ReconciliationFindingSeverity): StatusVariant {
  if (severity === 'P0') return 'danger';
  if (severity === 'P1') return 'warning';
  if (severity === 'P2') return 'info';
  return 'neutral';
}

function runStatusVariant(status: string): StatusVariant {
  if (status === 'succeeded') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'partial_success') return 'warning';
  if (status === 'running' || status === 'pending') return 'pending';
  return 'neutral';
}

function runStatusLabel(status: string): string {
  switch (status) {
    case 'succeeded':
      return 'Temiz';
    case 'failed':
      return 'P0 bulundu';
    case 'partial_success':
      return 'Sapma var';
    case 'running':
      return 'Çalışıyor';
    case 'pending':
      return 'Bekliyor';
    default:
      return status;
  }
}

function findingTypeLabel(type: string): string {
  switch (type) {
    case 'BIZ_RECON_L1_TENDER_MISSING':
      return 'Tender eksik';
    case 'BIZ_RECON_L1_TENDER_MISMATCH':
      return 'Tender toplamı farklı';
    case 'BIZ_RECON_L2_INTENT_MISSING':
      return 'Payment intent linki yok';
    case 'BIZ_RECON_L2_INTENT_NOT_FOUND':
      return 'Payment intent bulunamadı';
    case 'BIZ_RECON_L2_INTENT_NOT_CAPTURED':
      return 'Payment capture tamamlanmadı';
    case 'BIZ_RECON_L2_PAYABLE_MISMATCH':
      return 'Payment payable bağlantısı farklı';
    case 'BIZ_RECON_L2_TENANT_MISMATCH':
      return 'Payment tenant metadata farklı';
    case 'BIZ_RECON_L2_AMOUNT_MISMATCH':
      return 'Payment tutarı farklı';
    case 'BIZ_RECON_L3_BANK_LINE_NO_INTENT':
      return 'Banka satırı için payment intent yok';
    case 'BIZ_RECON_L3_INTENT_NO_BANK_LINE':
      return 'Payment intent settlement satırında yok';
    case 'BIZ_RECON_L3_AMOUNT_DRIFT_BEYOND_FEE':
      return 'Settlement tutar farkı eşik üstünde';
    case 'BIZ_RECON_L4_ETTN_MISSING':
      return 'Paid e-faturada ETTN eksik';
    case 'BIZ_RECON_L4_PROVIDER_REJECTED':
      return 'E-fatura provider reddetti';
    case 'BIZ_RECON_L4_LATE_ACCEPTANCE':
      return 'E-fatura provider kabulü gecikti';
    default:
      return type;
  }
}

function Metric({
  icon,
  tone,
  label,
  value,
  emphasis,
}: {
  icon: JSX.Element;
  tone: 'success' | 'warning' | 'danger' | 'neutral' | 'info';
  label: string;
  value: string;
  emphasis?: boolean;
}): JSX.Element {
  const toneClass = {
    success: 'bg-biz-success-subtle text-biz-success border-biz-success-border',
    warning: 'bg-biz-warning-subtle text-biz-warning border-biz-warning-border',
    danger: 'bg-biz-danger-subtle text-biz-danger border-biz-danger-border',
    neutral: 'bg-biz-muted text-biz-muted-foreground border-biz-border',
    info: 'bg-biz-info-subtle text-biz-info border-biz-info/30',
  }[tone];

  return (
    <Card>
      <CardBody className="p-biz-content">
        <div className="flex items-center gap-2 text-biz-xs font-semibold uppercase tracking-wide text-biz-muted-foreground">
          <span className={`flex h-8 w-8 items-center justify-center rounded-full border ${toneClass}`}>
            {icon}
          </span>
          {label}
        </div>
        <p className={`${emphasis ? 'text-biz-2xl' : 'text-biz-xl'} mt-biz-content font-bold biz-heading biz-numeric`}>
          {value}
        </p>
      </CardBody>
    </Card>
  );
}

const sampleBankLinesJson = JSON.stringify(
  [
    {
      source: 'iyzico',
      statementDate: today(),
      pspReference: 'iyzico-payment-id',
      bankReference: 'bank-line-id',
      amountMinor: 10000,
      currency: 'TRY',
    },
  ],
  null,
  2,
);

const sampleEfaturaAckJson = JSON.stringify(
  {
    invoiceId: 'invoice-uuid',
    providerStatus: 'accepted',
    ettn: '00000000-0000-0000-0000-000000000000',
  },
  null,
  2,
);

function normalizeBankLine(
  value: unknown,
  fallbackDate: string,
): ImportBankStatementLinePayload {
  const line = value as Partial<ImportBankStatementLinePayload>;
  return {
    source: line.source ?? 'iyzico',
    statementDate: line.statementDate ?? fallbackDate,
    pspReference: line.pspReference ?? null,
    bankReference: line.bankReference ?? null,
    amountMinor: Number(line.amountMinor),
    currency: line.currency ?? 'TRY',
    raw: line.raw ?? null,
  };
}

function FindingCard({
  finding,
  note,
  isResolving,
  onNoteChange,
  onResolve,
}: {
  finding: ReconciliationFinding;
  note: string;
  isResolving: boolean;
  onNoteChange: (value: string) => void;
  onResolve: (action: ResolveReconciliationFindingAction) => void;
}): JSX.Element {
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-surface p-biz-card">
      <div className="flex flex-col gap-biz-content md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-biz-content">
            <StatusBadge variant={severityVariant(finding.severity)} label={finding.severity} />
            <span className="rounded-full bg-biz-muted px-biz-content py-1 text-biz-xs font-semibold text-biz-muted-foreground">
              {finding.layer}
            </span>
            <span className="text-biz-xs text-biz-muted-foreground">
              {formatDateTime(finding.detectedAt)}
            </span>
          </div>
          <h3 className="mt-biz-content font-semibold biz-heading">
            {findingTypeLabel(finding.findingType)}
          </h3>
          <p className="mt-1 text-biz-sm text-biz-muted-foreground">
            {finding.sourceTable}
            {finding.sourceId ? ` · ${finding.sourceId.slice(0, 8)}` : ''}
          </p>
        </div>
        <div className="text-left md:text-right">
          <p className="text-biz-xs font-semibold uppercase tracking-wide text-biz-muted-foreground">
            Sapma
          </p>
          <p className="text-biz-xl font-bold biz-heading biz-numeric">
            {formatMoney(finding.deltaMinor / 100, finding.currency)}
          </p>
          <p className="text-biz-xs text-biz-muted-foreground">
            Beklenen {formatMoney((finding.expectedAmountMinor ?? 0) / 100, finding.currency)}
            {' '}· Gerçek {formatMoney((finding.actualAmountMinor ?? 0) / 100, finding.currency)}
          </p>
        </div>
      </div>

      <div className="mt-biz-content-lg grid gap-biz-content md:grid-cols-[1fr_auto]">
        <textarea
          value={note}
          onChange={(event) => onNoteChange(event.target.value)}
          placeholder="Çözüm notu: kasa sayımı, banka slip kontrolü, manuel düzeltme..."
          className="min-h-[96px] rounded-biz-card border border-biz-border bg-biz-surface-elevated px-biz-content py-biz-content text-biz-sm"
        />
        <div className="flex flex-col gap-biz-content">
          <Button
            type="button"
            size="sm"
            disabled={isResolving}
            onClick={() => onResolve('manual_resolved')}
          >
            Çözüldü
          </Button>
          <Button
            type="button"
            size="sm"
            intent="secondary"
            disabled={isResolving}
            onClick={() => onResolve('escalated')}
          >
            Eskale et
          </Button>
          <Button
            type="button"
            size="sm"
            intent="ghost"
            disabled={isResolving}
            onClick={() => onResolve('wont_fix')}
          >
            Waive
          </Button>
        </div>
      </div>
    </div>
  );
}
